/**
 * AuditorAssignmentCard Component
 *
 * Collapsible card showing weekly assignments for a single auditor.
 * Assignments are CS Audit records with this auditor assigned.
 * Client/Audit info is read-only (from Gobodo), only status/remarks are editable.
 */

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2, User, Search, ExternalLink } from 'lucide-react';
import AuditPickerModal, { SelectedAuditData } from './AuditPickerModal';

/**
 * Format ISO date (yyyy-MM-dd) for display as MM/DD/YYYY
 */
const formatDateForDisplay = (isoDate: string | undefined): string => {
  if (!isoDate) return '-';
  try {
    // Handle ISO datetime strings by extracting date part
    const datePart = isoDate.includes('T') ? isoDate.split('T')[0] : isoDate;
    const [year, month, day] = datePart.split('-');
    if (year && month && day) {
      return `${month}/${day}/${year}`;
    }
    // Fallback: try parsing as Date
    const date = new Date(isoDate);
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
      });
    }
    return isoDate;
  } catch {
    return isoDate;
  }
};

export interface WeeklyAssignment {
  id: string;
  weekStartDate: string;
  auditorId: string;
  auditorName: string;
  clientName: string;
  clientId: string;
  auditId?: string;
  auditName?: string;
  auditStatus: string;
  dueDate: string;
  status: 'not_started' | 'in_progress' | 'completed';
  remarks?: string;
  sortOrder: number;
}

interface AuditorAssignmentCardProps {
  auditorId: string;
  auditorName: string;
  assignments: WeeklyAssignment[];
  weekStartDate: string;
  onAddAssignment: (auditorId: string, auditData?: SelectedAuditData) => void;
  onUpdateAssignment: (assignment: WeeklyAssignment) => void;
  onDeleteAssignment: (assignmentId: string) => void;
  defaultExpanded?: boolean;
}

const STATUS_OPTIONS: { value: WeeklyAssignment['status']; label: string }[] = [
  { value: 'not_started', label: 'Not Started' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const getStatusBadgeStyles = (status: WeeklyAssignment['status']): string => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
  }
};

const AuditorAssignmentCard: React.FC<AuditorAssignmentCardProps> = ({
  auditorId,
  auditorName,
  assignments,
  weekStartDate,
  onAddAssignment,
  onUpdateAssignment,
  onDeleteAssignment,
  defaultExpanded = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const [isPickerOpen, setIsPickerOpen] = useState(false);

  // Handle audit selection from picker
  const handleAuditSelected = (auditData: SelectedAuditData) => {
    onAddAssignment(auditorId, auditData);
    setIsPickerOpen(false);
  };

  const completedCount = assignments.filter(a => a.status === 'completed').length;
  const totalCount = assignments.length;

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
      {/* Card Header - Clickable to expand/collapse */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="h-5 w-5 text-gray-400" />
          ) : (
            <ChevronRight className="h-5 w-5 text-gray-400" />
          )}
          <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
            <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
          </div>
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {auditorName}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500 dark:text-gray-400">
            {completedCount}/{totalCount} completed
          </span>
          <span className="px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300 rounded-full">
            {totalCount} assignment{totalCount !== 1 ? 's' : ''}
          </span>
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-gray-200 dark:border-gray-700">
          {/* Assignment Table */}
          {assignments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-900/50 text-left text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  <tr>
                    <th className="px-3 py-2 font-medium">Client</th>
                    <th className="px-3 py-2 font-medium">Audit</th>
                    <th className="px-3 py-2 font-medium">Phase/Type</th>
                    <th className="px-3 py-2 font-medium">Due Date</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 font-medium">Remarks</th>
                    <th className="px-3 py-2 font-medium w-16">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {assignments.map(assignment => (
                    <tr key={assignment.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/30">
                      {/* Client Name (read-only) */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-900 dark:text-gray-100 font-medium">
                            {assignment.clientName || 'Unknown'}
                          </span>
                          {assignment.clientId && (
                            <span className="text-xs text-gray-400">
                              ({assignment.clientId.slice(0, 8)}...)
                            </span>
                          )}
                        </div>
                      </td>

                      {/* Audit Name (read-only with link) */}
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1">
                          <span className="text-gray-900 dark:text-gray-100">
                            {assignment.auditName || assignment.auditId || '-'}
                          </span>
                          {assignment.auditId && (
                            <ExternalLink className="h-3 w-3 text-gray-400" />
                          )}
                        </div>
                      </td>

                      {/* Audit Status/Type (read-only) */}
                      <td className="px-3 py-2">
                        <span className="text-gray-700 dark:text-gray-300">
                          {assignment.auditStatus || '-'}
                        </span>
                      </td>

                      {/* Due Date (read-only, formatted as MM/DD/YYYY) */}
                      <td className="px-3 py-2">
                        <span className="text-gray-700 dark:text-gray-300">
                          {formatDateForDisplay(assignment.dueDate)}
                        </span>
                      </td>

                      {/* Status (editable) */}
                      <td className="px-3 py-2">
                        <select
                          value={assignment.status}
                          onChange={(e) => onUpdateAssignment({
                            ...assignment,
                            status: e.target.value as WeeklyAssignment['status']
                          })}
                          className={`px-2 py-1 text-xs font-medium rounded cursor-pointer ${getStatusBadgeStyles(assignment.status)}`}
                        >
                          {STATUS_OPTIONS.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                          ))}
                        </select>
                      </td>

                      {/* Remarks (editable) */}
                      <td className="px-3 py-2">
                        <input
                          type="text"
                          value={assignment.remarks || ''}
                          onChange={(e) => onUpdateAssignment({
                            ...assignment,
                            remarks: e.target.value
                          })}
                          className="w-full px-2 py-1 text-sm border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                          placeholder="Notes..."
                        />
                      </td>

                      {/* Actions (unassign) */}
                      <td className="px-3 py-2">
                        <button
                          onClick={() => onDeleteAssignment(assignment.id)}
                          className="p-1 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
                          title="Unassign audit from this auditor"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
              No audits assigned to this auditor
            </div>
          )}

          {/* Add Assignment Button */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
            <button
              onClick={() => setIsPickerOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
            >
              <Search className="h-4 w-4" />
              Assign Audit
            </button>
          </div>
        </div>
      )}

      {/* Audit Picker Modal */}
      <AuditPickerModal
        isOpen={isPickerOpen}
        onClose={() => setIsPickerOpen(false)}
        onSelect={handleAuditSelected}
        auditorIdFilter={auditorId}
      />
    </div>
  );
};

export default AuditorAssignmentCard;
